<template>
  <div class="p-sidebar">
    <UDrawer
      v-model:open="open"
      :title="title || 'Paneel'"
      direction="right"
      :handle="false"
      :content="{ onCloseAutoFocus: restoreTriggerFocus }"
      :ui="{
        overlay: 'z-60 bg-black/40',
        content: 'surface z-60 w-full max-w-full rounded-none md:max-w-96'
      }"
    >
      <PButton
        ref="trigger"
        :title="triggerLabel"
        :aria-label="triggerLabel"
        aria-haspopup="dialog"
        :aria-expanded="open"
        color="surface"
        :size="isSmallMq ? 'md' : 'xs'"
        :icon="['fas', 'bars']"
      />
      <template #content>
        <USidebar
          :title="title"
          collapsible="none"
          class="p-sidebar-panel min-h-0 w-full border-0!"
          :ui="{
            root: 'w-full [--sidebar-width:100%]',
            inner: 'bg-surface dark:bg-surface-dark',
            header: 'pt-8 sm:pt-0',
            body: 'py-0'
          }"
        >
          <template #header>
            <span class="flex-1 font-medium">{{ title }}</span>
            <PButton
              class="text-inherit! dark:text-inherit!"
              :icon="['fas', 'xmark']"
              aria-label="Menu sluiten"
              variant="ghost"
              color="surface"
              size="lg"
              @click="close"
            />
          </template>
          <slot :close="close" />

          <PNavigationMenu
            v-model="settingsMenuValue"
            :items="menuItems"
            :ui="navigationUi"
            orientation="vertical"
            color="surface"
            aria-label="Instellingen en acties"
          />

          <div class="grow"></div>

          <div class="space-y-2">
            <PlanningCacheToggle />
            <!-- Font size controls -->
            <div
              class="border-gray-lighten-2 flex items-center justify-between gap-x-2 rounded-md border-2 p-2 select-none"
            >
              <div class="mb-1 font-medium">Tekstgrootte:</div>
              <div class="flex gap-2">
                <PButton
                  :icon="['fas', 'minus']"
                  @click="decreaseFontSize"
                  title="Verklein tekst"
                  :disabled="currentFontSize === FONT_SIZES[0]"
                />
                <PButton
                  :icon="['fas', 'plus']"
                  @click="increaseFontSize"
                  title="Vergroot tekst"
                  :disabled="currentFontSize === FONT_SIZES[FONT_SIZES.length - 1]"
                />
              </div>
            </div>
            <div class="flex flex-col space-y-2 select-none">
              <!-- Push Notifications -->
              <div
                v-if="
                  isCapacitor &&
                  activeUser?.knownBusinessEntityContact?.hasRole(RoleTypes.SERVICE_PROVIDER)
                "
                class="border-gray-lighten-2 rounded-md border-2 p-2 select-none"
              >
                <div class="flex items-center justify-between">
                  <div>
                    <h3 class="font-medium">Meldingen</h3>
                    <p class="text-sm opacity-70">
                      Ontvang updates over wijzigingen in je planning
                    </p>
                  </div>
                  <PButton
                    :icon="['fas', notificationsAccess ? 'check' : 'bell']"
                    @click="onNotificationButtonClick"
                    class=""
                  />
                </div>
                <p class="text-gray-lighten-1 text-xs">
                  Status: {{ notificationsAccess ? "Toegestaan" : "Niet ingesteld" }}
                </p>
              </div>

              <!-- Location Tracking -->
              <div
                v-if="
                  isCapacitor &&
                  activeUser?.knownBusinessEntityContact?.hasRole(RoleTypes.SERVICE_PROVIDER)
                "
                class="border-gray-lighten-2 rounded-md border-2 p-2 select-none"
              >
                <div class="flex items-center justify-between">
                  <div>
                    <h3 class="font-medium">Locatie delen</h3>
                    <p class="text-sm opacity-70">Deel je locatie tijdens het werk</p>
                  </div>
                  <PButton
                    :icon="['fas', locationAccess === 'granted' ? 'check' : 'location-dot']"
                    @click="onLocationButtonClick"
                  />
                </div>
                <p class="text-gray-lighten-1 text-xs">
                  Status: {{ getPermissionStatusText(locationAccess) }}
                </p>
              </div>

              <!-- User info  -->
              <div class="flex flex-col gap-y-2">
                <div class="border-gray-lighten-2 rounded-md border-2 p-2 select-none">
                  <div v-if="activeUser?.fullName">
                    <span class="font-bold">{{ activeUser.fullName }}</span>
                  </div>
                  <div v-if="userRoles.length > 0">
                    <span> {{ userRoles.join(", ") }}</span>
                  </div>
                  <div
                    v-if="activeUser?.email"
                    class="text-gray-lighten-1 flex items-center space-x-2"
                  >
                    <FontAwesomeIcon :icon="['fas', 'envelope']" size="sm" />
                    <span>{{ activeUser.email }}</span>
                  </div>
                  <div
                    v-if="activeUser?.knownBusinessEntityContact?.phoneNumber"
                    class="text-gray-lighten-1 flex items-center space-x-2"
                  >
                    <FontAwesomeIcon :icon="['fas', 'phone']" size="sm" />
                    <span>{{
                      activeUser.knownBusinessEntityContact?.getNormalizedPhoneNumber()
                    }}</span>
                  </div>
                </div>
              </div>

              <!-- App Info Section -->

              <div
                class="border-gray-lighten-2 flex items-center justify-between rounded-md border p-2 select-none"
              >
                <div class="font-medium">App versie</div>
                <div class="text-gray-lighten-1">{{ appVersion }}</div>
              </div>
            </div>
          </div>
          <template v-if="$slots.footer" #footer><slot name="footer" :close="close" /></template>
        </USidebar>
      </template>
    </UDrawer>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, useTemplateRef, type VNode } from "vue"
import PButton from "@/components/elements/PButton.vue"
import PlanningCacheToggle from "@/cache/PlanningCacheToggle.vue"
import USidebar from "@nuxt/ui/components/Sidebar.vue"
import UDrawer from "@nuxt/ui/components/Drawer.vue"

import { useStorage } from "@vueuse/core"
import { storeToRefs } from "pinia"
import router from "@/router"
import PNavigationMenu from "@/components/navigation/PNavigationMenu.vue"
import type {
  PNavigationMenuItem,
  PNavigationMenuValue
} from "@/components/navigation/navigation-menu.types"
import { useAuthStore } from "@/store/auth.store"
import { UserTenantRepository } from "@/repositories/user-tenant-repository"
import type { IUserTenant } from "@/interfaces/api/user-tenant.interface"
import { useDeviceDetection } from "@/composables/utils/device-detection"
import { Capacitor } from "@capacitor/core"
import { Geolocation } from "@capacitor/geolocation"
import { useFirebaseMessaging } from "@/mobile_app/services/firebase-capacitor-service"
import { useDialogStore } from "@/store/dialog.store"
import { RoleTypes } from "@/enums/role-types.ts"
import { DialogTypes } from "@/interfaces/component/dialog.interface"
import { version } from "../../../package.json"
import { useLayoutColumnsStore } from "@/store/layout-columns.store"
import { isProductionEnvironment } from "@/helper/app/browser-helper.ts"
import { useUserSettingsStore } from "@/store/user-settings.store"
import { dark } from "@/helper/enums/theme-helper"
import { DarkTheme } from "@/enums/theme-choices"
import { initializeStatusBar } from "@/mobile_app/services/statusbar-service"
import useAuthentication from "@/composables/auth/authentication.ts"
import { getReplay, uiProfiler } from "@sentry/vue"
import {
  markSessionRecordingActive,
  markSessionRecordingInactive
} from "@/helper/app/session-recording-state"

withDefaults(defineProps<{ title?: string; triggerLabel?: string }>(), {
  title: "",
  triggerLabel: "Open menu"
})
const open = defineModel<boolean>("open", { default: false })
const trigger = useTemplateRef<InstanceType<typeof PButton>>("trigger")

function restoreTriggerFocus(event: Event) {
  event.preventDefault()
  const element = trigger.value?.$el
  // The trigger may be in a column that has just scrolled out of view.
  if (element instanceof HTMLElement) element.focus({ preventScroll: true })
}

defineSlots<{
  default?(props: { close: () => void }): VNode[]
  footer?(props: { close: () => void }): VNode[]
}>()
const navigationUi = {
  root: "gap-0",
  item: "border-t border-gray-lighten-2 dark:border-gray-lighten-2-dark",
  link: "px-2 py-2 text-base font-bold",
  linkLeadingIcon: "size-4",
  linkTrailingIcon: "size-3"
}
function close() {
  open.value = false
}

const authStore = useAuthStore()
const layoutColumnsStore = useLayoutColumnsStore()
const userSettingsStore = useUserSettingsStore()
const { activeUser } = storeToRefs(authStore)
const { showLayoutControls } = storeToRefs(layoutColumnsStore)
const { storedTheme } = storeToRefs(userSettingsStore)

// Device detection
const { isMobile, isSmallMq, isPWA, isCapacitor } = useDeviceDetection()

// Permission handling
const { hasPushPermission, initializeNativePush } = useFirebaseMessaging()
const dialogStore = useDialogStore()

// Change this to store the permission state
const notificationsAccess = ref(false)
const locationAccess = ref("prompt") // Default to 'prompt' until we check

const appVersion = ref(version) // Use the imported version from package.json

// Computed property to get user role names
const userRoles = computed(() => {
  if (!activeUser.value?.knownBusinessEntityContact?.roles) {
    return []
  }
  return activeUser.value.knownBusinessEntityContact.roles.map((role) => role.verboseName)
})

// Tenant switcher functionality
const userTenantRepository = new UserTenantRepository()
const switchingTenantId = ref<string | null>(null)

const userTenants = computed(() => {
  return activeUser.value?.userTenants ?? []
})

const tenantMenuItems = computed<PNavigationMenuItem[]>(() => {
  if (userTenants.value.length <= 1) return []

  return [
    {
      value: "tenants",
      label: activeUser.value?.activeTenant.name,
      description: "Bedrijf wijzigen",
      icon: ["fas", "building"],
      children: userTenants.value.map((userTenant) => {
        const isActive = userTenant.tenant.id === activeUser.value?.activeTenant.id
        return {
          value: userTenant.tenant.id,
          label: userTenant.tenant.name,
          description: isActive ? "Huidig bedrijf" : "Selecteer om van bedrijf te wisselen",
          icon: ["fas", isActive ? "check" : "arrow-right"],
          active: isActive,
          loading: switchingTenantId.value === userTenant.tenant.id,
          disabled: isActive || switchingTenantId.value !== null,
          onSelect: () => selectTenant(userTenant)
        }
      })
    }
  ]
})

const settingsMenuValue = ref<PNavigationMenuValue>([])

const darkModeMenuItem = computed<PNavigationMenuItem>(() => ({
  value: "dark-mode",
  label: "Donkere modus",
  description: dark[storedTheme.value].name,
  icon: ["fas", "circle-half-stroke"],
  children: Object.entries(dark).map(([key, option]) => {
    const theme = key as DarkTheme
    const isActive = storedTheme.value === theme
    return {
      value: theme,
      active: isActive,
      disabled: isActive,
      icon: ["fas", isActive ? "check" : "arrow-right"],
      label: option.name,
      description: option.description,
      onSelect: () => setDarkMode(theme)
    }
  })
}))

const authentication = useAuthentication()
function logout() {
  authentication.logoutRedirect()
}

// On-demand support recording (Session Replay + UI profiling). Session Replay
// is dormant on the web app (auto-recording disabled to avoid rrweb's
// memory/listener growth). The user starts a recording, reproduces the problem,
// and explicitly stops it — stopping is what flushes the replay and the CPU
// profile to Sentry. Reactive so the menu item can toggle between start/stop.
const isRecording = ref(false)

function startSessionRecording() {
  open.value = false

  const replay = getReplay()
  if (!replay) {
    dialogStore.currentDialog = {
      type: DialogTypes.OK,
      title: "Opname niet beschikbaar",
      body: "Sessie-opname kon niet worden gestart."
    }
    return
  }

  // Already recording (e.g. native auto-records) — start() would be a no-op.
  if (replay.getReplayId()) {
    dialogStore.currentDialog = {
      type: DialogTypes.OK,
      title: "Opname is al actief",
      body: "Er loopt al een sessie-opname."
    }
    return
  }

  try {
    // Enable tracing for the duration of this recording so the replay carries
    // performance metrics (see `tracesSampler` in plugins/index.ts). Must be set
    // before replay.start() so transactions overlap the replay window.
    markSessionRecordingActive()
    replay.start()
    // Also capture a CPU performance profile (flame graph) for this session —
    // the Sentry equivalent of a DevTools Performance snapshot.
    // Requires the `Document-Policy: js-profiling` header + a Chromium browser;
    // when unsupported the profiler simply no-ops and the replay still records.
    startPerformanceProfiling()
    isRecording.value = true
    dialogStore.currentDialog = {
      type: DialogTypes.OK,
      title: "Opname gestart",
      body: "Deze sessie wordt nu opgenomen. Reproduceer het probleem en kies daarna 'Opname stoppen' in het menu."
    }
  } catch {
    markSessionRecordingInactive()
    dialogStore.currentDialog = {
      type: DialogTypes.OK,
      title: "Opname mislukt",
      body: "Sessie-opname kon niet worden gestart."
    }
  }
}

// Stops the recording: flushes the CPU profile and the Session Replay to Sentry
// and returns tracing to its dormant state.
function stopSessionRecording() {
  open.value = false

  stopPerformanceProfiling()

  // replay.stop() finalizes and uploads the recording (best-effort).
  const replay = getReplay()
  Promise.resolve(replay?.stop()).catch(() => {})

  markSessionRecordingInactive()
  isRecording.value = false

  dialogStore.currentDialog = {
    type: DialogTypes.OK,
    title: "Opname gestopt",
    body: "De opname is naar support verstuurd."
  }
}

// Sentry UI profiling runs in "manual" lifecycle (see plugins/index.ts): a
// profile chunk is only flushed to Sentry on stopProfiler(). It starts when the
// recording begins and flushes when the user stops the recording. The `pagehide`
// listener is a best-effort safety net if the app is closed without stopping
// (unload sends aren't guaranteed — explicit stop is the reliable path).
let profilingActive = false

// Starts Sentry UI profiling. Guarded because the JS Self-Profiling API only
// exists in Chromium browsers served with the `Document-Policy: js-profiling`
// header (unavailable in the native webview / non-Chromium browsers).
function startPerformanceProfiling() {
  if (typeof (window as unknown as { Profiler?: unknown }).Profiler === "undefined") {
    // No JS Self-Profiling support (e.g. missing Document-Policy header or
    // non-Chromium browser / native webview) — skip silently, replay still runs.
    return
  }
  try {
    uiProfiler.startProfiler()
    profilingActive = true
    window.addEventListener("pagehide", stopPerformanceProfiling)
  } catch {
    // Profiling is best-effort; never block the session recording on it.
    profilingActive = false
  }
}

// Stops UI profiling and flushes the collected profile chunk to Sentry. Safe to
// call multiple times; subsequent calls are no-ops.
function stopPerformanceProfiling() {
  if (!profilingActive) {
    return
  }
  profilingActive = false
  window.removeEventListener("pagehide", stopPerformanceProfiling)
  try {
    uiProfiler.stopProfiler()
  } catch {
    // best-effort flush
  }
}

function goToManagementSite() {
  let managementUrl
  if (isProductionEnvironment()) {
    const currentOrigin = window.location.origin
    managementUrl = `${currentOrigin}/d/admin/`
  } else {
    const apiUrl = import.meta.env.VITE_API_URL
    managementUrl = `${apiUrl}/admin/`
  }
  window.open(managementUrl, "_blank")
}

function goToDashboard() {
  let dashboardUrl
  if (isProductionEnvironment()) {
    const currentOrigin = window.location.origin
    dashboardUrl = `${currentOrigin}/d/administration/admin/dashboard/`
  } else {
    const apiUrl = import.meta.env.VITE_API_URL
    dashboardUrl = `${apiUrl}/administration/admin/dashboard/`
  }
  window.open(dashboardUrl, "_blank")
}

function goToTemplates() {
  router.push({ name: "templates" })
  open.value = false
}

function reloadPage() {
  location.reload()
}

function enableLayoutControls() {
  showLayoutControls.value = true
  open.value = false
}

function getPermissionStatusText(status: string) {
  switch (status) {
    case "granted":
      return "Toegestaan"
    case "denied":
      return "Geweigerd"
    case "prompt":
      return "Niet ingesteld"
    default:
      return "Onbekend"
  }
}

// Update the permission state when mounted and after changes
async function checkNotificationPermission() {
  if (Capacitor.isNativePlatform()) {
    notificationsAccess.value = await hasPushPermission()
  }
}

async function onNotificationButtonClick() {
  await initializeNativePush()
  await checkNotificationPermission() // Update state after requesting permission
}

async function onLocationButtonClick() {
  try {
    // Check permission first
    const permissionStatus = await Geolocation.checkPermissions()

    if (permissionStatus.location === "granted") {
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000
      })
      console.log(`Locatie: ${position.coords.latitude}, ${position.coords.longitude}`)
      dialogStore.currentDialog = {
        type: DialogTypes.OK,
        title: "Locatie delen ingeschakeld",
        body: "Je locatie wordt nu gedeeld tijdens het werk."
      }
    } else {
      // Request permission if not granted
      const requestResult = await Geolocation.requestPermissions()
      locationAccess.value = requestResult.location

      if (requestResult.location === "granted") {
        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 10000
        })
        console.log(`Locatie: ${position.coords.latitude}, ${position.coords.longitude}`)
        dialogStore.currentDialog = {
          type: DialogTypes.OK,
          title: "Locatie delen ingeschakeld",
          body: "Je locatie wordt nu gedeeld tijdens het werk."
        }
      } else {
        dialogStore.currentDialog = {
          type: DialogTypes.OK,
          title: "Locatie delen uitgeschakeld",
          body: "Om je locatie te delen, moet je locatie toegang aanzetten. Ga naar de instellingen van de Peanuts app, kies 'Rechten' en zet 'Locatie delen' aan."
        }
      }
    }
  } catch (error) {
    console.error("Error accessing location:", error)
    dialogStore.currentDialog = {
      type: DialogTypes.OK,
      title: "Locatie delen uitgeschakeld",
      body: "Er is een fout opgetreden bij het toegang krijgen tot je locatie. Controleer je apparaatinstellingen."
    }
  }
}

// Check location permissions
async function checkLocationPermissions() {
  try {
    const permissionStatus = await Geolocation.checkPermissions()
    locationAccess.value = permissionStatus.location
  } catch (error) {
    console.error("Error checking location permissions:", error)
    locationAccess.value = "prompt"
  }
}

async function selectTenant(tenant: IUserTenant) {
  if (switchingTenantId.value || !tenant.tenant.id) {
    return
  }

  switchingTenantId.value = tenant.tenant.id

  try {
    await userTenantRepository.updateActiveTenant(tenant.tenant.id)
    settingsMenuValue.value = []
    open.value = false
    window.location.reload()
  } catch (error) {
    console.error("Failed to switch tenant:", error)
    switchingTenantId.value = null
  }
}

async function setDarkMode(theme: DarkTheme) {
  userSettingsStore.setDarkMode(theme)
  settingsMenuValue.value = []
  await initializeStatusBar(true)
}

const menuItems = computed<PNavigationMenuItem[]>(() => {
  const items: PNavigationMenuItem[] = [
    {
      label: "Kolom toevoegen",
      description: "Open 'nieuw kolom' weergave",
      icon: "table-columns",
      onSelect: enableLayoutControls,
      class: "sm:hidden",
      hide: !activeUser.value?.knownBusinessEntityContact?.hasRole(RoleTypes.PLANNER)
    },
    {
      label: "Beheer",
      description: "Django sitebeheer",
      icon: "screwdriver-wrench",
      onSelect: goToManagementSite,
      hide:
        !activeUser.value?.isSuperuser &&
        !activeUser.value?.knownBusinessEntityContact?.hasRole(RoleTypes.PLANNER) // Planner can access the surcharge admin
    },
    {
      label: "Administratie",
      description: "Naar de administratie",
      icon: "file-lines",
      onSelect: goToDashboard,
      hide: !activeUser.value?.knownBusinessEntityContact?.hasRole(RoleTypes.PLANNER)
    },
    {
      label: "Dashboard",
      description: "Naar het dashboard",
      icon: "chart-bar",
      onSelect: () => {
        router.push({ path: "/dashboard" })
        open.value = false
      },
      hide: !activeUser.value?.knownBusinessEntityContact?.hasRole(RoleTypes.PLANNER)
    },
    {
      label: "Templates",
      description: "Vaste diensten en werkzaamheden",
      icon: "copy",
      onSelect: goToTemplates,
      hide: !activeUser.value?.knownBusinessEntityContact?.hasRole(RoleTypes.SERVICE_PROVIDER)
    },
    {
      label: "Uitloggen",
      description: "Sessie afsluiten",
      icon: "right-from-bracket",
      onSelect: logout
    },
    {
      label: isRecording.value ? "Opname stoppen" : "Sessie opnemen",
      description: isRecording.value
        ? "Stop de opname en verstuur naar support"
        : "Neem deze sessie op voor support",
      icon: isRecording.value ? "circle-stop" : "camera",
      onSelect: isRecording.value ? stopSessionRecording : startSessionRecording
    }
  ]

  if (isMobile.value && (isPWA.value || isCapacitor.value)) {
    items.unshift({
      label: "App verversen",
      description: "Laad de app opnieuw",
      icon: "arrows-rotate",
      onSelect: reloadPage
    })
  }

  return [...tenantMenuItems.value, darkModeMenuItem.value, ...items.filter((item) => !item.hide)]
})

const FONT_SIZES = [16, 18, 20]
const DEFAULT_FONT_SIZE = 16

const currentFontSize = useStorage("AppSettingsFontSize", DEFAULT_FONT_SIZE)

function updateFontSize(size: (typeof FONT_SIZES)[number]) {
  if (FONT_SIZES.includes(size)) {
    currentFontSize.value = size
    document.documentElement.style.fontSize = `${size}px`
  }
}

function increaseFontSize() {
  const currentIndex = FONT_SIZES.indexOf(currentFontSize.value as (typeof FONT_SIZES)[number])
  if (currentIndex < FONT_SIZES.length - 1) {
    updateFontSize(FONT_SIZES[currentIndex + 1])
  }
}
function decreaseFontSize() {
  const currentIndex = FONT_SIZES.indexOf(currentFontSize.value as (typeof FONT_SIZES)[number])
  if (currentIndex > 0) {
    updateFontSize(FONT_SIZES[currentIndex - 1])
  }
}

onMounted(async () => {
  updateFontSize(currentFontSize.value as (typeof FONT_SIZES)[number])

  // Check initial permissions
  await checkNotificationPermission()
  await checkLocationPermissions()
})
</script>

<style></style>
